import React, { useState, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';

import {
  announcementCreateSchema,
  announcementUpdateSchema,
} from '../../schemas';
import { getSchoolsForAnnouncementTargeting } from '../../services/announcementsService';
import {
  AnnouncementCreateFormData,
  AnnouncementUpdateFormData,
  Announcement,
} from '../../types';
import { Button } from '../common/Button';
import { Input, Select } from '../forms';

interface AnnouncementFormProps {
  announcement?: Announcement;
  onSubmit: (
    data: AnnouncementCreateFormData | AnnouncementUpdateFormData
  ) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface School {
  id: string;
  name: string;
  district_name?: string;
}

export const AnnouncementForm: React.FC<AnnouncementFormProps> = ({
  announcement,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [schools, setSchools] = useState<School[]>([]);
  const [loadingSchools, setLoadingSchools] = useState(false);

  const isEdit = !!announcement;
  const schema = isEdit ? announcementUpdateSchema : announcementCreateSchema;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: announcement
      ? {
          title: announcement.title,
          content: announcement.content,
          summary: announcement.summary || '',
          type: announcement.type,
          priority: announcement.priority,
          target_roles: announcement.target_roles || [],
          target_grades: announcement.target_grades || [],
          channels: announcement.channels,
          scheduled_for: announcement.scheduled_for || '',
          expires_at: announcement.expires_at || '',
          school_id: announcement.school_id || '',
          tags: announcement.tags || [],
        }
      : {
          title: '',
          content: '',
          summary: '',
          type: 'general',
          priority: 'medium',
          target_roles: [],
          target_grades: [],
          channels: ['email'],
          scheduled_for: '',
          expires_at: '',
          school_id: '',
          tags: [],
        },
  });

  // Watch for type changes to set default priority
  const watchedType = watch('type');
  const watchedPriority = watch('priority');

  // Load schools on component mount
  useEffect(() => {
    const loadSchools = async () => {
      try {
        setLoadingSchools(true);
        const schoolsData = await getSchoolsForAnnouncementTargeting();
        setSchools(schoolsData);
      } catch (error) {
        console.error('Failed to load schools:', error);
      } finally {
        setLoadingSchools(false);
      }
    };

    loadSchools();
  }, []);

  // Auto-set priority based on type
  useEffect(() => {
    if (watchedType === 'emergency') {
      // Don't auto-change priority if it's already set to critical
      if (watchedPriority !== 'critical') {
        // This would require setValue from useForm, but we'll handle it in the form
      }
    }
  }, [watchedType, watchedPriority]);

  const handleFormSubmit = (
    data: AnnouncementCreateFormData | AnnouncementUpdateFormData
  ) => {
    // Convert empty strings to undefined for optional fields
    const cleanedData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        value === '' ? undefined : value,
      ])
    );
    onSubmit(cleanedData);
  };

  // Options for dropdowns
  const typeOptions = [
    { label: 'General', value: 'general' },
    { label: 'Emergency', value: 'emergency' },
    { label: 'Event', value: 'event' },
    { label: 'Academic', value: 'academic' },
    { label: 'Policy', value: 'policy' },
    { label: 'Reminder', value: 'reminder' },
  ];

  const priorityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
    { label: 'Critical', value: 'critical' },
  ];

  const roleOptions = [
    { label: 'All Users', value: 'all' },
    { label: 'Administrators', value: 'admin' },
    { label: 'Teachers', value: 'teacher' },
    { label: 'Students', value: 'student' },
    { label: 'Parents', value: 'parent' },
    { label: 'Staff', value: 'staff' },
  ];

  const gradeOptions = [
    { label: 'All Grades', value: 'all' },
    { label: 'Pre-K', value: 'pre-k' },
    { label: 'Kindergarten', value: 'k' },
    { label: 'Grade 1', value: '1' },
    { label: 'Grade 2', value: '2' },
    { label: 'Grade 3', value: '3' },
    { label: 'Grade 4', value: '4' },
    { label: 'Grade 5', value: '5' },
    { label: 'Grade 6', value: '6' },
    { label: 'Grade 7', value: '7' },
    { label: 'Grade 8', value: '8' },
    { label: 'Grade 9', value: '9' },
    { label: 'Grade 10', value: '10' },
    { label: 'Grade 11', value: '11' },
    { label: 'Grade 12', value: '12' },
  ];

  const channelOptions = [
    { label: 'Email', value: 'email' },
    { label: 'SMS', value: 'sms' },
    { label: 'Push Notification', value: 'push' },
    { label: 'In-App Message', value: 'in_app' },
    { label: 'Website Banner', value: 'banner' },
  ];

  const schoolOptions = schools.map(school => ({
    label: school.district_name
      ? `${school.name} (${school.district_name})`
      : school.name,
    value: school.id,
  }));

  // Get current date for datetime-local inputs
  const now = new Date();
  const currentDateTime = new Date(
    now.getTime() - now.getTimezoneOffset() * 60000
  )
    .toISOString()
    .slice(0, 16);

  return (
    <div className="h-full flex flex-col">
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="h-full flex flex-col"
      >
        {/* Scrollable form content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Title */}
              <div className="md:col-span-2">
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="Announcement Title *"
                      placeholder="Enter a clear, descriptive title"
                      error={errors.title?.message}
                    />
                  )}
                />
              </div>

              {/* Type */}
              <div>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ''}
                      label="Announcement Type *"
                      options={typeOptions}
                      placeholder="Select announcement type"
                      error={errors.type?.message}
                    />
                  )}
                />
              </div>

              {/* Priority */}
              <div>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ''}
                      label="Priority Level *"
                      options={priorityOptions}
                      placeholder="Select priority level"
                      error={errors.priority?.message}
                    />
                  )}
                />
              </div>
            </div>

            {/* Content */}
            <div className="space-y-4">
              {/* Summary */}
              <div>
                <Controller
                  name="summary"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="Summary"
                      placeholder="Brief summary of the announcement (optional)"
                      helperText="A short description that will appear in notification previews"
                      error={errors.summary?.message}
                    />
                  )}
                />
              </div>

              {/* Content */}
              <div>
                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <div className="w-full">
                      <label
                        htmlFor="content"
                        className={`block text-sm font-medium mb-1 ${
                          errors.content ? 'text-red-700' : 'text-gray-700'
                        }`}
                      >
                        Announcement Content *
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <textarea
                        {...field}
                        id="content"
                        rows={6}
                        placeholder="Enter the full announcement content..."
                        className={`w-full px-3 py-2 border rounded-md transition-colors duration-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.content
                            ? 'border-red-500 focus:border-red-500'
                            : 'border-gray-300 focus:border-blue-500'
                        }`}
                        aria-invalid={!!errors.content}
                        aria-describedby={
                          errors.content ? 'content-error' : undefined
                        }
                      />
                      {errors.content && (
                        <div
                          id="content-error"
                          className="mt-1 text-sm text-red-600"
                          role="alert"
                        >
                          {errors.content.message}
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>

            {/* Target Audience */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Target Audience
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Target Roles */}
                <div>
                  <Controller
                    name="target_roles"
                    control={control}
                    render={({ field }) => (
                      <div className="w-full">
                        <p className="block text-sm font-medium text-gray-700 mb-1">
                          Target Roles *
                          <span className="text-red-500 ml-1">*</span>
                        </p>
                        <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                          {roleOptions.map(option => (
                            <label
                              key={option.value}
                              className="flex items-center"
                            >
                              <input
                                type="checkbox"
                                value={option.value}
                                checked={
                                  field.value?.includes(option.value) || false
                                }
                                onChange={e => {
                                  const currentValues = field.value || [];
                                  if (e.target.checked) {
                                    field.onChange([
                                      ...currentValues,
                                      option.value,
                                    ]);
                                  } else {
                                    field.onChange(
                                      currentValues.filter(
                                        (v: string | undefined) => v !== option.value
                                      )
                                    );
                                  }
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">
                                {option.label}
                              </span>
                            </label>
                          ))}
                        </div>
                        {errors.target_roles && (
                          <div
                            className="mt-1 text-sm text-red-600"
                            role="alert"
                          >
                            {errors.target_roles.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>

                {/* Target Grades */}
                <div>
                  <Controller
                    name="target_grades"
                    control={control}
                    render={({ field }) => (
                      <div className="w-full">
                        <p className="block text-sm font-medium text-gray-700 mb-1">
                          Target Grades (Optional)
                        </p>
                        <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                          {gradeOptions.map(option => (
                            <label
                              key={option.value}
                              className="flex items-center"
                            >
                              <input
                                type="checkbox"
                                value={option.value}
                                checked={
                                  field.value?.includes(option.value) || false
                                }
                                onChange={e => {
                                  const currentValues = field.value || [];
                                  if (e.target.checked) {
                                    field.onChange([
                                      ...currentValues,
                                      option.value,
                                    ]);
                                  } else {
                                    field.onChange(
                                      currentValues.filter(
                                        (v: string | undefined) => v !== option.value
                                      )
                                    );
                                  }
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">
                                {option.label}
                              </span>
                            </label>
                          ))}
                        </div>
                        {errors.target_grades && (
                          <div
                            className="mt-1 text-sm text-red-600"
                            role="alert"
                          >
                            {errors.target_grades.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* School Selection */}
              <div>
                <Controller
                  name="school_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ''}
                      label="School (Optional)"
                      options={schoolOptions}
                      placeholder="Select a specific school (leave empty for all schools)"
                      error={errors.school_id?.message}
                      loading={loadingSchools}
                    />
                  )}
                />
              </div>
            </div>

            {/* Delivery Channels */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Delivery Channels
              </h3>

              <Controller
                name="channels"
                control={control}
                render={({ field }) => (
                  <div className="w-full">
                    <p className="block text-sm font-medium text-gray-700 mb-1">
                      Select Delivery Channels *
                      <span className="text-red-500 ml-1">*</span>
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {channelOptions.map(option => (
                        <label
                          key={option.value}
                          className="flex items-center p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            value={option.value}
                            checked={
                              field.value?.includes(option.value) || false
                            }
                            onChange={e => {
                              const currentValues = field.value || [];
                              if (e.target.checked) {
                                field.onChange([
                                  ...currentValues,
                                  option.value,
                                ]);
                              } else {
                                field.onChange(
                                  currentValues.filter(
                                    (v: string | undefined) => v !== option.value
                                  )
                                );
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors.channels && (
                      <div className="mt-1 text-sm text-red-600" role="alert">
                        {errors.channels.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>

            {/* Scheduling */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Scheduling</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Scheduled For */}
                <div>
                  <Controller
                    name="scheduled_for"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={field.value || ''}
                        type="datetime-local"
                        label="Schedule For (Optional)"
                        helperText="Leave empty to send immediately"
                        error={errors.scheduled_for?.message}
                        min={currentDateTime}
                      />
                    )}
                  />
                </div>

                {/* Expires At */}
                <div>
                  <Controller
                    name="expires_at"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={field.value || ''}
                        type="datetime-local"
                        label="Expires At (Optional)"
                        helperText="When this announcement should no longer be displayed"
                        error={errors.expires_at?.message}
                        min={currentDateTime}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Tags */}
            <div>
              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <div className="w-full">
                    <label
                      htmlFor="announcement-tags"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Tags (Optional)
                    </label>
                    <input
                      id="announcement-tags"
                      type="text"
                      placeholder="Enter tags separated by commas (e.g., weather, closure, important)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      onChange={e => {
                        const tags = e.target.value
                          .split(',')
                          .map(tag => tag.trim())
                          .filter(tag => tag.length > 0);
                        field.onChange(tags);
                      }}
                      defaultValue={field.value?.join(', ') || ''}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Separate multiple tags with commas
                    </p>
                    {errors.tags && (
                      <div className="mt-1 text-sm text-red-600" role="alert">
                        {errors.tags.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
          </div>
        </div>

        {/* Form Actions - Sticky at bottom */}
        <div className="flex-shrink-0 flex justify-end space-x-3 p-6 pt-4 border-t border-gray-200 bg-white">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting || isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || isLoading}
            loading={isSubmitting || isLoading}
          >
            {isEdit ? 'Update Announcement' : 'Create Announcement'}
          </Button>
        </div>
      </form>
    </div>
  );
};
